import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Tag, Save, Trash2, RefreshCw, Calendar, Megaphone, Check } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

/**
 * AffiliatePromoAdminCard — iter190
 *
 * One-place admin control panel to schedule a time-boxed discount on any
 * enabled affiliate brand (Teva UK, Absolute Snow, FINDRA, Camping World UK…).
 * Reuses the existing `PATCH /admin/affiliate/brands/{slug}` endpoint —
 * we just set the `promo` object with `code / headline / detail /
 * valid_from / valid_to / dest_url`.
 *
 * The public `<AffiliatePromoBanner>` (mounted in App.js) auto-pulls any
 * brand whose promo is inside its valid window and displays a black
 * top-of-site strip with the discount + affiliate CTA. No further code
 * edits needed to run a flash sale.
 */
export default function AffiliatePromoAdminCard() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/affiliate/brands");
      setBrands(data?.brands || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mb-6 p-4 sm:p-5 border hairline bg-white" data-testid="affiliate-promo-admin">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="eyebrow mb-0.5 flex items-center gap-2">
            <Tag className="w-3 h-3" /> Affiliate flash sales
          </div>
          <div className="font-display text-lg leading-tight">Schedule a promo · any brand</div>
          <p className="text-xs text-[#666] mt-0.5 max-w-2xl">
            Pick a brand, drop in the code and dates. Live goes up automatically at the "from" time; the top-of-site banner and the 4 promo reels update the moment the window opens. No redeploy needed.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] mono uppercase tracking-wider border hairline hover:border-[#ff4500] hover:bg-[#fff4ee] disabled:opacity-50"
          data-testid="promo-admin-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : (
        <div className="space-y-3">
          {brands.map((b) => (
            <BrandPromoRow key={b.slug} brand={b} onSaved={load} />
          ))}
          {brands.length === 0 && (
            <div className="p-3 text-[11px] mono text-[#888] italic">No affiliate brands yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

const asDateTimeLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  } catch { return ""; }
};

const fromDateTimeLocal = (v) => {
  if (!v) return null;
  // Interpret entered local time AS UTC (the field label makes that clear).
  return `${v}:00Z`;
};

function BrandPromoRow({ brand, onSaved }) {
  const promo = brand?.promo || {};
  const [code, setCode] = useState(promo.code || "");
  const [headline, setHeadline] = useState(promo.headline || "");
  const [detail, setDetail] = useState(promo.detail || "");
  const [destUrl, setDestUrl] = useState(promo.dest_url || brand.root_url || "");
  const [from, setFrom] = useState(asDateTimeLocal(promo.valid_from));
  const [to, setTo] = useState(asDateTimeLocal(promo.valid_to));
  const [saving, setSaving] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  const [announceStatus, setAnnounceStatus] = useState(null);
  const [sendEmail, setSendEmail] = useState(false);
  const now = new Date();
  const activeNow = promo.code && promo.valid_from && promo.valid_to
    ? (new Date(promo.valid_from) <= now && now <= new Date(promo.valid_to))
    : false;

  useEffect(() => {
    let cancelled = false;
    if (!promo?.code) { setAnnounceStatus(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/affiliate/brands/${brand.slug}/announce-promo/status`);
        if (!cancelled) setAnnounceStatus(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [brand.slug, promo?.code]);

  const save = async () => {
    if (!code.trim() || !headline.trim() || !from || !to) {
      toast.info("Fill in code · headline · from · to");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/affiliate/brands/${brand.slug}`, {
        promo: {
          code: code.trim().toUpperCase(),
          headline: headline.trim(),
          detail: detail.trim(),
          valid_from: fromDateTimeLocal(from),
          valid_to: fromDateTimeLocal(to),
          dest_url: destUrl.trim() || brand.root_url,
        },
      });
      toast.success(`${brand.name} promo saved · goes live at ${from} UTC`);
      onSaved?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Clear the active promo on ${brand.name}?`)) return;
    setSaving(true);
    try {
      await api.patch(`/admin/affiliate/brands/${brand.slug}`, { promo: null });
      toast.success(`${brand.name} promo cleared`);
      setCode(""); setHeadline(""); setDetail(""); setFrom(""); setTo("");
      onSaved?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSaving(false); }
  };

  const announce = async () => {
    const withEmail = !!sendEmail;
    const confirmMsg = withEmail
      ? `Announce the ${brand.name} ${promo?.code || ""} promo AND email every deliverable member?`
      : `Post an in-app announcement for the ${brand.name} ${promo?.code || ""} promo? (No email.)`;
    if (!window.confirm(confirmMsg)) return;
    setAnnouncing(true);
    try {
      const { data } = await api.post(
        `/admin/affiliate/brands/${brand.slug}/announce-promo`,
        { send_email: withEmail },
      );
      toast.success(
        `Announced · ${data.recipients_notified} bells` +
        (data.skipped_email ? "" : ` · ${data.emails_sent} emails sent` + (data.emails_failed ? ` (${data.emails_failed} failed)` : "")),
      );
      // Refresh audit pill
      try {
        const { data: st } = await api.get(`/admin/affiliate/brands/${brand.slug}/announce-promo/status`);
        setAnnounceStatus(st);
      } catch { /* silent */ }
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setAnnouncing(false); }
  };

  return (
    <div className="border hairline p-3 bg-[#fafafa]" data-testid={`promo-admin-row-${brand.slug}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="font-display text-sm truncate">{brand.name}</div>
          <span className="text-[10px] mono uppercase tracking-wider text-[#888] shrink-0">{brand.slug}</span>
          {activeNow && (
            <span className="text-[10px] mono uppercase tracking-wider bg-[#0a7f3f] text-white px-1.5 py-0.5 shrink-0">LIVE NOW</span>
          )}
          {!activeNow && promo?.code && (
            <span className="text-[10px] mono uppercase tracking-wider bg-[#eee] text-[#555] px-1.5 py-0.5 shrink-0">Scheduled / expired</span>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mb-2">
        <label className="block">
          <span className="eyebrow block mb-1">Code</span>
          <input
            className="cc-input mono uppercase tracking-widest text-sm"
            value={code}
            onChange={(e) => setCode(e.target.value.slice(0, 32))}
            placeholder="OUTLET15"
            data-testid={`promo-admin-code-${brand.slug}`}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-1">Headline</span>
          <input
            className="cc-input text-sm"
            value={headline}
            onChange={(e) => setHeadline(e.target.value.slice(0, 80))}
            placeholder="Extra 15% off outlet"
            data-testid={`promo-admin-headline-${brand.slug}`}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="eyebrow block mb-1">Detail (shown in banner + reel)</span>
          <input
            className="cc-input text-sm"
            value={detail}
            onChange={(e) => setDetail(e.target.value.slice(0, 200))}
            placeholder="Use code OUTLET15 at checkout for an extra 15% off every outlet product."
            data-testid={`promo-admin-detail-${brand.slug}`}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="eyebrow block mb-1">Destination URL (deep-link)</span>
          <input
            className="cc-input mono text-xs"
            value={destUrl}
            onChange={(e) => setDestUrl(e.target.value)}
            placeholder={brand.root_url}
            data-testid={`promo-admin-dest-${brand.slug}`}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-1 inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> From · UTC</span>
          <input
            type="datetime-local"
            className="cc-input text-sm mono"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid={`promo-admin-from-${brand.slug}`}
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-1 inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> To · UTC</span>
          <input
            type="datetime-local"
            className="cc-input text-sm mono"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid={`promo-admin-to-${brand.slug}`}
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary text-xs inline-flex items-center gap-1.5"
          data-testid={`promo-admin-save-${brand.slug}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Save
        </button>
        {promo?.code && (
          <button
            onClick={clear}
            disabled={saving}
            className="text-xs inline-flex items-center gap-1.5 border hairline px-3 py-1.5 hover:bg-red-50 hover:border-red-300"
            data-testid={`promo-admin-clear-${brand.slug}`}
          >
            <Trash2 className="w-3 h-3" /> Clear
          </button>
        )}
        {activeNow && (
          <>
            <div className="w-full sm:w-auto border-l hairline pl-2 flex flex-wrap items-center gap-2 ml-0 sm:ml-1">
              <label className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  data-testid={`promo-admin-email-toggle-${brand.slug}`}
                />
                Also email members
              </label>
              <button
                onClick={announce}
                disabled={announcing}
                className="text-xs inline-flex items-center gap-1.5 bg-[#0a7f3f] text-white font-black uppercase tracking-widest px-3 py-1.5 shadow-[0_3px_0_0_#083c1e] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#083c1e] transition-transform disabled:opacity-60"
                data-testid={`promo-admin-announce-${brand.slug}`}
              >
                {announcing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Megaphone className="w-3 h-3" />}
                Announce
              </button>
            </div>
            {announceStatus?.announced && announceStatus?.code === (promo?.code || "").toUpperCase() && (
              <span
                className="text-[10px] mono uppercase tracking-wider inline-flex items-center gap-1 text-[#0a7f3f]"
                title={announceStatus?.detail?.announced_at}
                data-testid={`promo-admin-announced-pill-${brand.slug}`}
              >
                <Check className="w-3 h-3" /> Announced · {announceStatus?.detail?.recipients_notified || 0} bell{announceStatus?.detail?.emails_sent ? ` · ${announceStatus.detail.emails_sent} email` : ""}
              </span>
            )}
          </>
        )}
      </div>
      <p className="text-[10px] mono text-[#888] mt-2">
        Times are UTC. Enter <strong>02:00</strong> for 03:00 BST (British Summer Time).
      </p>
    </div>
  );
}
